import React, { useState } from 'react'
import { get } from 'lodash'
import moment from 'moment'
import api, { NumberType, IEmployee } from '@api'
import {
  Classes,
  ControlGroup,
  FormGroup,
  HTMLSelect,
  InputGroup,
  Intent,
  Position,
  Switch,
  Tabs,
  Tab,
} from '@blueprintjs/core'
import {
  Button,
  Confirmation,
  Details,
  Editable,
  LocationSelect,
  PrettyNumber,
  MultiEmployeeSelect,
  Icon,
  Flex,
  Dialog,
  prettyDate,
} from '@ui'
import { Tooltip2 } from '@blueprintjs/popover2'
import { RouteComponentProps } from '@reach/router'
import useLease, { ILeaseHook } from './hooks/useLease'
import QuickActions from './QuickActions'
import Form, { requiredFields } from './Form'
import { Currencies } from '@constants'
import LeaseEvents from './LeaseEvents'
import { useUser } from '@hooks'
import { sqFtToSqMeters } from '@utils'
import css from './leaseManagement.module.scss'

interface IProps extends RouteComponentProps<{ leaseId: string }> {
  reload?: () => void
}

interface IInputProps {
  path: string
  label: React.ReactNode
  hook: ILeaseHook
  numberType?: NumberType
  hint?: any
  'data-input'?: string
}

function TextDetail({ hook, path, label, 'data-input': dataInput }: IInputProps) {
  return (
    <Details.Text
      {...{ editing: hook.isEditing, label }}
      error={get(hook.errors, path)}
      value={get(hook.lease, path)}
      onChange={(e: any) => hook.update(path, e.target.value)}
      required={!!requiredFields.find((f) => f.key === path)}
      data-input={dataInput}
    />
  )
}

type TabId = 'main' | 'events' | 'notifications'

function NumberDetail({
  hook,
  hint,
  path,
  label,
  numberType,
  'data-input': dataInput,
  convertToSqM,
}: IInputProps & { convertToSqM?: boolean }) {
  return (
    <Details.Number
      {...{ editing: hook.isEditing, label, numberType, hint }}
      error={get(hook.errors, path)}
      value={convertToSqM ? sqFtToSqMeters(get(hook.lease, path) || 0) : get(hook.lease, path)}
      onChange={(e: any) => hook.update(path, e.target.value)}
      required={!!requiredFields.find((f) => f.key === path)}
      data-input={dataInput}
    />
  )
}

export default function LeaseDetails({ leaseId, navigate, reload }: IProps) {
  const [tabId, setTabId] = useState<TabId>('main')
  const hook = useLease(leaseId)
  const { user } = useUser()
  const isUsingSqM = user?.context.units === 'sqm'
  const sectionProps = { hook }
  const onClose = () => {
    reload?.()
    navigate?.(window.location.pathname)
  }

  const Title = () => {
    if (hook.isEditing) return null
    return (
      <>
        <div className={Classes.DIALOG_HEADER} />
        <div className="mb4" style={{ fontSize: 32 }}>
          {get(hook.lease, 'title') || get(hook.lease, 'floor.building.address')}
        </div>
      </>
    )
  }

  const title = () => {
    if (!hook.isEditing) return undefined
    if (get(hook.lease, 'id')) return 'Update Lease Details'
    return 'Create New Lease'
  }
  return (
    <Dialog
      isOpen
      title={title()}
      usePortal
      isCloseButtonShown={hook.isEditing ? false : undefined}
      onClose={() => {
        if (hook.isEditing && hook.isDirty) return
        onClose()
      }}
      canOutsideClickClose={false}
      canEscapeKeyClose={false}
      style={{ minWidth: 1050 }}
      toolbar={
        <QuickActions
          editing={hook.isEditing}
          dirty={hook.isDirty}
          lease={hook.lease}
          onEdit={() => hook.setEditing(!hook.isEditing)}
          onDelete={onClose}
          onClose={onClose}
        />
      }
      body={
        <div className={Classes.DIALOG_BODY}>
          <Title />
          <Form hook={hook}>
            <MainCard {...sectionProps} />
            {hook.isEditing ? (
              <div>
                <div className={css.sdetail_row}>
                  <Dates {...sectionProps} withStartDate />
                  <Costs {...sectionProps} />
                </div>
                <div className={css.sdetail_row}>
                  <SQFT {...sectionProps} isEditing />
                  <ContactInfo {...sectionProps} />
                </div>
                <div className={css.sdetail_row}>
                  <AdditionalInformation {...sectionProps} />
                  <UploadedFiles {...sectionProps} />
                </div>
              </div>
            ) : (
              <Tabs
                large
                id="lease-tabs"
                onChange={(newTabId: TabId) => setTabId(newTabId)}
                selectedTabId={tabId}
              >
                <Tab
                  id="main"
                  title="Details"
                  panel={
                    <div>
                      <div className={css.sdetail_row}>
                        <Dates
                          {...sectionProps}
                          withStartDate
                          toNotifications={() => setTabId('notifications')}
                        />
                        <Costs {...sectionProps} />
                      </div>
                      <div className={css.sdetail_row}>
                        <SQFT {...sectionProps} isSqM={isUsingSqM} />
                        <ContactInfo {...sectionProps} />
                      </div>
                      <div className={css.sdetail_row}>
                        <AdditionalInformation {...sectionProps} />
                        <UploadedFiles {...sectionProps} />
                      </div>
                    </div>
                  }
                />
                <Tab
                  id="events"
                  title="Events"
                  panel={
                    <LeaseEvents
                      {...sectionProps}
                      toNotifications={() => setTabId('notifications')}
                    />
                  }
                />
                <Tab
                  id="notifications"
                  title="Notifications"
                  panel={<NotificationsPanel {...sectionProps} />}
                />
              </Tabs>
            )}

            {hook.isEditing && (
              <div className="f f-end mt3">
                <Confirmation
                  enabled={hook.isDirty}
                  text="Discard unsaved changes?"
                  onConfirm={() => {
                    hook.reload()
                    get(hook, 'lease.id') ? hook.setEditing(false) : onClose()
                  }}
                >
                  <Button
                    className="mr2"
                    text="Cancel"
                    onClick={() => {
                      if (hook.isDirty) return
                      get(hook, 'lease.id') ? hook.setEditing(false) : onClose()
                    }}
                  />
                </Confirmation>
                <Button
                  disabled={!hook.isDirty}
                  intent={Intent.PRIMARY}
                  loading={hook.isSaving}
                  data-button="save_lease"
                  text="Save"
                  type="submit"
                />
              </div>
            )}
          </Form>
        </div>
      }
    />
  )
}

function MainCard({ hook }: ISectionProps) {
  return (
    <>
      {hook.isEditing && (
        <Details.Row>
          <TextDetail hook={hook} label="Title" path="title" data-input="title" />
        </Details.Row>
      )}
      <Details.Row>
        <Details.Label required {...{ editing: hook.isEditing, label: 'Location' }} />
        <Editable editing={hook.isEditing}>
          <span>
            {get(hook.lease, 'floor.building.address') || get(hook.lease, 'building.address', '-')}
          </span>
          <FormGroup
            helperText={hook.errors['floor|building']}
            intent={hook.errors['floor|building'] ? Intent.DANGER : undefined}
          >
            <LocationSelect
              includeOldBuildings
              buttonProps={{
                style: {
                  border: hook.errors.floor ? '1px solid var(--dark-red)' : '',
                },
                'data-button': 'location_select',
              }}
              onChange={(location: any) => {
                if ('building' in location) {
                  hook.update('floor', location)
                  hook.update('building', null)
                  return
                }
                hook.update('building', location)
                hook.update('floor', null)
              }}
              value={get(hook.lease, 'floor.id') || get(hook.lease, 'building.id')}
              mode={LocationSelect.Mode.SINGLE_BUILDING_FLOOR}
              className="mr2"
            />
          </FormGroup>
        </Editable>
      </Details.Row>
      {Boolean(get(hook, 'lease.floor')) && (
        <Details.Row>
          <TextDetail hook={hook} label="Suite" path="metadata.suite" />
        </Details.Row>
      )}
    </>
  )
}

interface IDateDetail {
  path: string
  label: string
  noticePath?: string
  tooltip: string
  hook: ILeaseHook
  'data-input'?: string
}
function DateDetail({
  path,
  label,
  noticePath,
  tooltip,
  hook,
  'data-input': dataInput,
}: IDateDetail) {
  let append = null
  let value = null
  if (get(hook.lease, path)) {
    value = prettyDate({ value: get(hook.lease, path) })
  }

  if (noticePath && value) {
    const disabled = !get(hook.lease, path) || moment().isAfter(moment(get(hook, path)))
    append = (
      <Tooltip2
        content={disabled ? `Set the ${label.toLowerCase()} in the future to enable.` : tooltip}
        position={Position.TOP}
      >
        <Switch
          className="m0 ml2"
          disabled={disabled}
          onChange={async (e: any) => {
            hook.update(noticePath, e.target.checked, !hook.isEditing, !hook.isEditing)
          }}
          label={`Notifications ${get(hook.lease, noticePath) ? 'On' : 'Off'}`}
          checked={get(hook.lease, noticePath)}
        />
      </Tooltip2>
    )
  }

  return (
    <Details.Date
      colorize
      {...{ append, label, editing: hook.isEditing, value }}
      error={get(hook.errors, path)}
      onChange={(date: Date) => hook.update(path, date)}
      required={!!requiredFields.find((f) => f.key === path)}
      data-input={dataInput}
    />
  )
}

function Dates({
  hook,
  withStartDate,
  toNotifications,
}: ISectionProps & { withStartDate?: boolean; toNotifications?: () => void }) {
  return (
    <div className="pr4">
      <Details.Header
        label="Dates"
        iconName="calendar"
        leftItem={
          toNotifications ? (
            <div className={css.link} style={{ marginLeft: 20 }} onClick={toNotifications}>
              set notification recipients
            </div>
          ) : undefined
        }
      />
      {withStartDate && (
        <Details.Row>
          <DateDetail
            hook={hook}
            path="startDate"
            label="Start Date"
            tooltip="Get notification emails 60, 30, 15, and 1 days before this date."
            data-input="start_date"
          />
        </Details.Row>
      )}
      <Details.Row>
        <DateDetail
          hook={hook}
          path="reminderDate"
          label="Reminder Date"
          noticePath="reminderAlert"
          tooltip="Enable to get a notification email on this date."
        />
      </Details.Row>
      <Details.Row>
        <DateDetail
          hook={hook}
          path="noticeDate"
          label="Notice Date"
          noticePath="noticeAlert"
          tooltip="Enable to get notification emails 60, 30, 15, and 1 days before this date."
        />
      </Details.Row>
      <Details.Row>
        <DateDetail
          hook={hook}
          path="effectiveDate"
          label="Effective Date"
          noticePath="effectiveAlert"
          tooltip="Enable to get a notification email on this date."
        />
      </Details.Row>
      <Details.Row>
        <DateDetail
          hook={hook}
          path="endDate"
          label="Expiration Date"
          noticePath="endAlert"
          tooltip="Enable to get notification emails 60, 30, 15, and 1 days before this date."
          data-input="expiration_date"
        />
      </Details.Row>
    </div>
  )
}
function Costs({ hook }: ISectionProps) {
  return (
    <div>
      <Details.Header label="Costs" iconName="dollar-sign" />
      {!hook.isEditing && (
        <Details.Row>
          <NumberDetail
            hook={hook}
            label="Monthly Cost"
            path="monthlyCost"
            numberType={NumberType.DOLLAR}
          />
        </Details.Row>
      )}
      <Details.Row>
        <NumberDetail
          hook={hook}
          label="Annual Cost"
          path="cost"
          numberType={NumberType.DOLLAR}
          data-input="annual_cost"
        />
      </Details.Row>
      {!hook.isEditing && (
        <Details.Row>
          <NumberDetail
            hook={hook}
            label="Total Cost"
            path="totalCost"
            numberType={NumberType.DOLLAR}
          />
        </Details.Row>
      )}
      <Details.Row>
        <Details.Label editing={hook.isEditing} label="Other Currency" />
        <Editable editing={hook.isEditing}>
          <>
            <PrettyNumber value={get(hook.lease, 'metadata.otherCost')} />
            <span className="ml1">{get(hook.lease, 'metadata.otherCurrencyType')}</span>
          </>
          <FormGroup
            {...{
              helperText: get(hook.errors, 'metadata.otherCost'),
              intent: get(hook.errors, 'metadata.otherCost') ? Intent.DANGER : undefined,
            }}
          >
            <ControlGroup>
              <HTMLSelect
                options={Currencies}
                value={get(hook.lease, 'metadata.otherCurrencyType')}
                onChange={(e: any) => hook.update('metadata.otherCurrencyType', e.target.value)}
              />
              <InputGroup
                value={get(hook.lease, 'metadata.otherCost', '')}
                onChange={(e: any) => hook.update('metadata.otherCost', e.target.value)}
                type="number"
                style={{ height: '100%' }}
              />
            </ControlGroup>
          </FormGroup>
        </Editable>
      </Details.Row>
      {!hook.isEditing && (
        <Details.Row>
          <NumberDetail
            hook={hook}
            hint={
              <div className="centered">
                Lease cost divided by lease area <br />
                (Or the floor's area size if lease area is not set)
              </div>
            }
            label="Cost Per SQFT"
            path="costPerSqft"
            numberType={NumberType.DOLLAR}
          />
        </Details.Row>
      )}
    </div>
  )
}
function SQFT({
  hook,
  isEditing,
  isSqM,
}: ISectionProps & { isEditing?: boolean; isSqM?: boolean }) {
  const usingSqM = !isEditing && isSqM
  return (
    <div className="pr4">
      <Details.Header label={usingSqM ? <SqMLabel /> : 'SQFT'} iconName="square" />
      <Details.Row>
        <NumberDetail
          hook={hook}
          label={
            usingSqM ? (
              <span>
                Lease Area (<SqMLabel />)
              </span>
            ) : (
              'Lease Area (sqft)'
            )
          }
          path="metadata.leaseArea"
          convertToSqM={usingSqM}
        />
      </Details.Row>
      <Details.Row>
        <NumberDetail
          hook={hook}
          label={
            usingSqM ? (
              <span>
                Usable Area (<SqMLabel />)
              </span>
            ) : (
              'Usable Area (sqft)'
            )
          }
          path="metadata.usableLeaseArea"
          convertToSqM={usingSqM}
        />
      </Details.Row>
    </div>
  )
}
function ContactInfo({ hook }: ISectionProps) {
  return (
    <div>
      <Details.Header label="Contact Info" iconName="id-card" />
      <Details.Row>
        <TextDetail hook={hook} label="Owner" path="owner" />
      </Details.Row>
      <Details.Row>
        <TextDetail hook={hook} label="Name" path="contactName" />
      </Details.Row>
      <Details.Row>
        <TextDetail hook={hook} label="Contact Info" path="contactInfo" />
      </Details.Row>
    </div>
  )
}
function AdditionalInformation({ hook }: ISectionProps) {
  return (
    <div className="pr4">
      <Details.Header label="Additional Information" iconName="info" />
      <Details.Row>
        <TextDetail hook={hook} label="Property Type" path="metadata.propertyType" />
      </Details.Row>
      <Details.Row>
        <TextDetail hook={hook} label="Lease Premises" path="metadata.leasePremises" />
      </Details.Row>
      <Details.Row>
        <TextDetail hook={hook} label="Renewal Options" path="metadata.renewalOptions" />
      </Details.Row>
      <Details.Row>
        <TextDetail hook={hook} label="Notes" path="notes" />
      </Details.Row>
    </div>
  )
}
function UploadedFiles({ hook }: ISectionProps) {
  return (
    <div>
      <Details.Header label="Uploaded Files" iconName="file-upload" />
      <Details.Files
        files={get(hook.lease, 'files', [])}
        localFiles={hook.localFiles}
        editing={hook.isEditing}
        onAdd={(newFiles) => {
          hook.setLocalFiles([...newFiles, ...hook.localFiles])
        }}
        onRemove={(file) => {
          hook.setLocalFiles(hook.localFiles.filter((f) => f !== file))
          const _files = get(hook.lease, 'files', [])
          hook.update(
            'files',
            _files.filter((f) => f !== file)
          )
          if ('id' in file) api.lease.assets.remove(file.id)
        }}
      />
    </div>
  )
}

function NotificationsPanel({ hook }: ISectionProps) {
  return (
    <>
      <Dates hook={hook} />

      <div style={{ width: '100%' }}>
        <Details.Header
          label="Recipients"
          iconName="inbox"
          leftItem={
            <Flex align="baseline">
              <Tooltip2
                content={
                  <div style={{ maxWidth: 475 }}>
                    Add recipients to receive email notifications for lease dates and events. This
                    custom list will override the default 'all admins' recipients list.
                  </div>
                }
              >
                <Icon
                  style={{
                    color: '#999999',
                    fontSize: 18,
                    margin: '15px 0 10px 5px',
                  }}
                  name="info-circle"
                />
              </Tooltip2>
            </Flex>
          }
        />
        <MultiEmployeeSelect
          values={hook.lease?.employees || []}
          onChange={(items: IEmployee[]) => hook.update('employees', items, true, true)}
          noTitle
          onClear={() => hook.update('employees', [], true, true)}
        />
      </div>
    </>
  )
}

const SqMLabel = () => (
  <span>
    M<sup>2</sup>
  </span>
)

export interface ISectionProps {
  hook: ILeaseHook
}
